import './Badge.css'

import { clsx } from 'clsx'
import type { ReactElement } from 'react'

import type { SemanticType } from '#design-system/semanticType'

interface BadgeBaseProps {
  /** Semantic color of the badge fill. Defaults to neutral. `emphasis` is a high-contrast ink fill. */
  semanticType?: SemanticType | 'emphasis' | undefined
  /** Accessible name — e.g. a count badge reading "3 unread" rather than just "3". */
  'aria-label'?: string | undefined
  /** Additional classes for semantics or style overrides. */
  className?: string | undefined
}

/** Count badge — a number reads as a pill, never carries a leading icon. */
interface NumberBadgeProps {
  label: number
  icon?: never
}

/** Label badge — a category/attribute/state string, with an optional leading icon. */
interface StringBadgeProps {
  label: string
  /** Optional leading icon, shown before the label. */
  icon?: ReactElement | undefined
}

export type BadgeProps = BadgeBaseProps & (NumberBadgeProps | StringBadgeProps)

const MAX_COUNT = 99

/**
 * Badge is a non-interactive label for categories, attributes, object states (string), or counts
 * (number → pill, with `99+` overflow). For anything clickable or removable, use Chip.
 */
export const Badge = ({
  label,
  icon,
  semanticType,
  'aria-label': ariaLabel,
  className,
}: BadgeProps): ReactElement => {
  const isCount = typeof label === 'number'
  const display = isCount && label > MAX_COUNT ? `${String(MAX_COUNT)}+` : label
  return (
    <span
      aria-label={ariaLabel}
      className={clsx('doss-badge', semanticType?.toLowerCase(), { count: isCount }, className)}
    >
      {!isCount && icon ? icon : null}
      <span className="label">{display}</span>
    </span>
  )
}
